import { type CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import { defaultCategoryGroups } from "@/domain/categories";
import { useModifiers } from "@/domain/modifiers/use-modifiers";
import { profileModifiers } from "@/domain/profile-resolver/index";
import { builtinGames, defaultGame, gameOrder } from "@/games/index";
import { CommandCard } from "@/games/sc2/CommandCard";
import { iconGlyph } from "@/games/sc2/sc2-icons";
import {
  contextsFor,
  parentContext,
  validContextId,
  validRosterId,
} from "@/games/sc2/sc2-registry";
import { useImportPipeline } from "@/hardware/import-pipeline/use-import-pipeline";
import { isBoardLayerMapping } from "@/hardware/keyboard-layouts/index";
import { activeLayout, layoutGroups } from "@/hardware/keyboard-layouts/picker";
import { builtins } from "@/hardware/keyboards/builtin";
import { renameBoardLayer } from "@/hardware/keyboards/keyboard-import";
import { modifierFamily } from "@/hardware/modifier-family";
import type { CatalogEntry } from "@/platform/catalog";
import { UPLOADED_ID } from "@/platform/catalog/active-setup";
import { FACETED_SC2_IDS, FACETED_SC2_SETUPS } from "@/platform/catalog/sc2-setups";
import type { LensView } from "@/platform/share/link";
import { type KeyValueStore, localStorageStore } from "@/platform/storage/storage";
import { BindingsList } from "@/render/bindings-list/BindingsList";
import type { ListRow } from "@/render/bindings-list/groups";
import { ChangesList } from "@/render/compare/ChangesList";
import { type CompareSide, CompareView } from "@/render/compare/CompareView";
import type { DiffRow } from "@/render/compare/diff";
import { rowLatchCombo } from "@/render/compare/flashlight";
import { useCompareFlashlight } from "@/render/compare/use-compare-flashlight";
import { AppHeader } from "@/render/components/AppHeader";
import { ContextSelect } from "@/render/components/ContextSelect";
import { DefaultsToggle } from "@/render/components/DefaultsToggle";
import { DropOverlay } from "@/render/components/DropOverlay";
import { ImportModal } from "@/render/components/ImportModal";
import { KeyboardSelect } from "@/render/components/KeyboardSelect";
import { LayoutPick } from "@/render/components/LayoutPick";
import { ModifierStrip } from "@/render/components/ModifierStrip";
import { PlusNote } from "@/render/components/PlusNote";
import { PresetDownload } from "@/render/components/PresetDownload";
import { ProfileDownload } from "@/render/components/ProfileDownload";
import { RapidFireToggle } from "@/render/components/RapidFireToggle";
import { SetupPicker, type SetupPickerProps } from "@/render/components/SetupPicker";
import { ShareLinkButton } from "@/render/components/ShareLinkButton";
import { Surfaces } from "@/render/components/Surfaces";
import { Toasts } from "@/render/components/Toasts";
import { UploadButton } from "@/render/components/UploadButton";
import { useMouseButtons } from "@/render/mouse-map/use-mouse-buttons";
import { Readout } from "@/render/readout/Readout";
import { useReadout } from "@/render/readout/use-readout";
import { useTweaks } from "@/render/tweaks/use-tweaks";
import { useActiveSetup } from "./use-active-setup";
import { useCompare } from "./use-compare";
import { useDrillNav } from "./use-drill-nav";
import { DEFAULT_BOARD_ID, usePageState } from "./use-page-state";
import { usePressFeedback } from "./use-press-feedback";
import { useShareLink } from "./use-share-link";
import { useSide } from "./use-side";
import { useSurfaceSizing } from "./use-surface-sizing";

// the browser-localStorage seam, created once. A prerender or a test injects its own
// KeyValueStore; everything else uses this singleton so the persist effects keep a stable store.
const defaultStore = localStorageStore();

export function VisualizerPage({
  storage = defaultStore,
  preset,
  onNavigate,
  lensView,
}: {
  storage?: KeyValueStore;
  preset?: CatalogEntry;
  onNavigate?: (to: string) => void;
  lensView?: LensView;
}) {
  // applies the fixed Steel (dark) visual styling to the document as a side effect; the app
  // is dark-only, so there is no user-controlled theme to read back (light dropped).
  useTweaks();
  // every persisted slice of page state (where-I-left-off), bundled behind one hook.
  const {
    boardId,
    setBoardId,
    gameId,
    setGameId,
    sc2SourceId,
    setSc2SourceId,
    profileSel,
    setProfileSel,
    imported,
    setImported,
    layoutSel,
    setLayoutSel,
    uploaded,
    setUploaded,
    uploadedProfile,
    setUploadedProfile,
    mode,
    setMode,
    race,
    setRace,
    contextSel,
    setContextSel,
    flatContextSel,
    setFlatContextSel,
  } = usePageState(storage);
  // the ephemeral lens override carried by a share link: a shared Keyboard + Keyboard
  // Layout shown for THIS view only, seeded once from the URL and never written to storage, so
  // opening someone's link never repaints the recipient's own saved board (the lens is
  // the viewer's own). Selecting a board in the picker adopts it (persists + clears the override);
  // tweaking the layout while viewing stays ephemeral. A lens NAMING a board that does not
  // resolve is dropped WHOLE at the seed: applying it would displace the viewer's board with the
  // app default and blank the Keyboard picker, and applying only its layout half would put the
  // sender's layout on a board they never saw while pickLayout keeps treating the session as a
  // shared view (picks silently never persist). Links carry built-in ids only (emission is
  // gated), and the lookup is own-key so a hostile ?kbd naming an Object.prototype member
  // ("constructor") is dropped too, not resolved to an inherited function that crashes the lens
  // chain. A layout-only lens (no kbd) stays the designed peek on the viewer's own board.
  const [lensOverride, setLensOverride] = useState<{ kbd?: string; layout?: string } | null>(() => {
    if (!lensView?.kbd && !lensView?.layout) return null;
    if (lensView.kbd && !Object.hasOwn(builtins, lensView.kbd)) return null;
    return { kbd: lensView.kbd, layout: lensView.layout };
  });

  // the bindings panel: open/collapsed, the isolated group, the hovered
  // row, and the press transient. A stale dimId after a profile switch simply stops
  // matching any group, the same derive-validity pattern as contextSel above
  const [listOpen, setListOpen] = useState(true);
  const [dimId, setDimId] = useState<string | null>(null);
  const [hoverKey, setHoverKey] = useState<ListRow | null>(null);
  // the search flashlight: codes matched by the bindings-list search, board+mouse dim to them
  const [searchCodes, setSearchCodes] = useState<string[] | null>(null);
  // the single press-feedback system: the depressed codes plus pressFlash, behind one
  // hook so a row / cap / chip click and a physical keypress all feed one transient.
  const { pressed, pressFlash } = usePressFeedback();
  // the rapid-fire overlay: off by default, shown only for SC2. Its toggle lives in the SC2
  // view-options group in the control bar; it drives the board ring plus the readout / list tags.
  const [rapidFireOn, setRapidFireOn] = useState(false);
  // hide the Standard defaults filled in under an upload: show only the player's own
  // bindings. Off by default (render all), ephemeral like rapidFireOn.
  const [hideDefaults, setHideDefaults] = useState(false);
  // select an SC2 Preset in place: force SC2 + that source. Used by the deep-link preload effect.
  const loadPreset = useCallback(
    (id: string) => {
      setGameId("sc2");
      setSc2SourceId(id);
    },
    [setGameId, setSc2SourceId],
  );

  // which side an upload applies to: set by a header upload button, or by a drop (B while comparing,
  // A otherwise). Shared between compare and the import pipeline, so it stays in the page; the rest of
  // the compare A/B state lives in useCompare (called below, once the A lens is resolved).
  const [uploadTarget, setUploadTarget] = useState<"A" | "B">("A");

  // the active Game. Resolution is one generic projector for every Game; the per-Game software
  // CHOOSING lives in the ActiveSetup seam (platform/catalog/active-setup), so `sc2` gates
  // only StarCraft II's own rendering features - the bespoke command-card Context tree, rapid-fire,
  // and drill-down - never the selection or resolution path, which are unified.
  const game = builtinGames[gameId] ?? defaultGame;
  const sc2 = game.id === "sc2";
  // the A side's resolved software choice: the picker's option list, the active source/Profile,
  // its pin, its published Preset, its file download, and the lens name-key, on one discriminated
  // value. A stale selection (an upload cleared, the Game switched away) has already derived back
  // to the landing default inside the seam.
  const setup = useActiveSetup({
    game,
    sc2SourceId,
    sc2Upload: uploaded,
    selectedProfileId: profileSel[gameId],
    simpleUpload: uploadedProfile[gameId] ?? null,
  });
  // the simple-Game Profile this view renders (null for SC2, which resolves via its source); the
  // flat-Context selector below keys off its populated Contexts.
  const profile = setup.kind === "profile" ? setup.profile : null;
  // a board-bound Profile/source pins its lens. Derived, never stored - it sits ABOVE the lens
  // chain, so switching back to a board-portable Profile restores the viewer's own lens with no
  // teardown.
  const activePin = setup.pin;
  // the active source's published Preset, if any (a downloadable TheCore setup; null under a pin),
  // and a simple Game's verbatim source-file download (Destiny's cvars.xml; pin-independent).
  const downloadEntry = setup.preset;
  const profileDownload = setup.download;

  // the effective Keyboard for this view: a board-bound pin wins, then a share-link override
  // (ephemeral, seed-validated above), then the persisted pick - so a board-bound Profile renders
  // on its own board, a shared link on the sender's, and neither overwrites the viewer's stored
  // selection. The lens and Software derived off this choice live in useSide (called below,
  // shared with the compare B side).
  const effBoardId = activePin?.keyboard ?? lensOverride?.kbd ?? boardId;
  // the session-imported entry specifically (not a built-in fallback): only it permits a layer rename.
  const importedEntry = imported[effBoardId];
  // a persisted boardId can outlive its board (boards blob evicted while the id survived, or a
  // built-in removed): snap it back to the default on the render that finds it unresolvable, so the
  // picker selection and the rendered board never diverge. Checked against the PERSISTED id, not the
  // override, so a transient share-link board never triggers a reset of the viewer's own selection.
  useEffect(() => {
    // own-key checks: a tampered persisted id naming an Object.prototype member must read as
    // unknown (and snap back), not as an inherited truthy function
    if (!Object.hasOwn(imported, boardId) && !Object.hasOwn(builtins, boardId))
      setBoardId(DEFAULT_BOARD_ID);
  }, [imported, boardId, setBoardId]);
  // a persisted gameId can name a Game no longer in the picker (Deadlock: hidden from gameOrder but
  // kept in builtinGames), so a returning user who last viewed it would otherwise render a game the
  // picker cannot represent. Snap it back to the default - the same derive-validity the boardId snap
  // above uses, and the stale-id derivation resolveActiveSetup applies to source/Profile selections.
  useEffect(() => {
    if (!gameOrder.some((id) => id === gameId)) setGameId(defaultGame.id);
  }, [gameId, setGameId]);

  // a Preset deep-link boots the app preloaded with that setup: force SC2 and the Preset's
  // source on mount (and when the route hands a different Preset), overriding the persisted
  // Game+Profile selection. The lens (board / layout) stays the viewer's own, so a Preset
  // opens on their own keyboard. `preset` is a stable catalog reference (resolveByRoute), so
  // this re-runs only on a real Preset change, never reverting a later user Profile switch.
  useEffect(() => {
    if (preset) loadPreset(preset.id);
  }, [preset, loadPreset]);

  // the second command-card axis: a Race in MP/campaign, a Commander in co-op. `race` is
  // the raw persisted selection; `roster` snaps it to one the current mode offers, so a mode switch
  // renders a valid roster while the raw pick revives when its mode returns (mirrors validContextId).
  const roster = sc2 ? validRosterId(mode, race) : race;
  const context = validContextId(roster, mode, contextSel);
  const contexts = contextsFor(roster, mode);
  // the flat-Context selector: the Game-declared Contexts the active Profile actually
  // populates, shown only when more than one (Destiny's On Foot / Vehicle - both its Default and
  // Edennil populate both). Keying the offered list off the Profile keeps the selector from listing a
  // Context the active Profile has no overlay for. SC2 keeps its bespoke command card; Deadlock has
  // one implicit root.
  const populatedContexts =
    !sc2 && game.contexts && profile?.contexts
      ? game.contexts.filter((c) => profile?.contexts?.[c.id])
      : [];
  const flatContexts = populatedContexts.length > 1 ? populatedContexts : null;
  const flatContextId =
    flatContexts?.find((c) => c.id === flatContextSel[gameId])?.id ??
    flatContexts?.[0]?.id ??
    "default";
  // the Context id the projector resolves against: SC2's race-qualified command-card context, the
  // active flat Context, or the single implicit root for a one-Context Game.
  const contextId = sc2 ? `${roster}/${context}` : flatContextId;
  // categories and grouping come straight from the active Game - SC2 carries its own curated
  // taxonomy; a simple Game with no explicit groups gets one group per Category
  const categories = game.categories;
  const groups = useMemo(() => game.groups ?? defaultCategoryGroups(game.categories), [game]);
  // a dimId left over from the previous grouping matches no group: it derives to
  // inactive everywhere (board and panel both), instead of being reset by an effect
  const activeDimId = dimId !== null && groups.some((g) => g.id === dimId) ? dimId : null;
  const dimMembers = activeDimId
    ? (groups.find((g) => g.id === activeDimId)?.members ?? null)
    : null;

  const { mods, held, toggleLatch, toggleModifier, clearLatched, latchCombo } = useModifiers();
  // the A side, derived and rendered through the shared useSide hook (lens + Software + render
  // pipeline); always enabled. The compare B side below calls the SAME hook gated on `comparing`, so
  // the two derive identically instead of as two hand-synced copies.
  const {
    entry,
    board,
    keyboardLayout,
    plusUnavailable,
    activeText,
    downloadLayout,
    table,
    layers,
    activeModifierLayer,
    comboHasExact,
    marks,
    keycaps,
    zones,
  } = useSide({
    setup,
    boardId: effBoardId,
    layoutId: activePin?.layer ?? lensOverride?.layout ?? layoutSel[effBoardId],
    imported,
    contextId,
    mods,
    categories,
    rapidFireOn,
    hideDefaults,
    enabled: true,
  });
  // the compare B side: a second Profile through its OWN Keyboard and Keyboard Layout, owned end to end
  // by useCompare (its B-only state, the B render bundle through the SAME useSide as A, and the diff).
  // Resolved here, after the A lens, so "Same as A" can follow the A-side effective board+layout.
  // Destructured into the page's own names, so the rest of the body reads B exactly as it did inline.
  const {
    comparing,
    setComparing,
    sc2SourceBId,
    setSc2SourceBId,
    setUploadedB,
    boardBId,
    pickBoardB,
    boardBPinned,
    layoutBValue,
    pickLayoutB,
    hoverDiffId,
    setHoverDiffId,
    setupB,
    sameBoardB,
    entryB,
    boardB,
    tableB,
    layersB,
    comboHasExactB,
    keycapsB,
    zonesB,
    plusUnavailableB,
    diff,
  } = useCompare({
    lensView,
    game,
    setup,
    imported,
    contextId,
    mods,
    categories,
    rapidFireOn,
    hideDefaults,
    effBoardId,
    keyboardLayout,
    layers,
  });
  // the modifier-strip toggles: the families this Profile actually binds
  const profileMods = useMemo(() => profileModifiers(layers), [layers]);
  // the search/hover flashlight: a hovered row wins over the search
  const flashlightCodes = hoverKey ? hoverKey.keys.map((k) => k.code) : searchCodes;

  // ids are imported-N and monotonic; seed the pipeline counter past any restored board so a
  // new upload never reuses a persisted id. The pipeline reads this only at mount,
  // when `imported` already holds the restored set, so recomputing it per render is harmless.
  let maxImportN = 0;
  for (const id of Object.keys(imported)) {
    const m = /^imported-(\d+)$/.exec(id);
    if (m?.[1]) maxImportN = Math.max(maxImportN, Number(m[1]));
  }

  // import preview coverage stays pinned to the base layer, stable across mod swaps
  const pipeline = useImportPipeline(
    layers.base,
    (applied) => {
      if (applied.kind === "profile") {
        const r = applied.result;
        const { text, name } = r;
        if (r.format === "destiny-cvars") {
          // a simple Game's "my own" upload: switch to that Game, store its cvars text per Game, and
          // select the uploaded Profile so the picker reflects the board. A-side only - the simple-Game
          // compare B-side mirrors A, so there is no B upload target to route to.
          setGameId(r.gameId);
          setUploadedProfile((prev) => ({ ...prev, [r.gameId]: { text, name } }));
          setProfileSel((prev) => ({ ...prev, [r.gameId]: UPLOADED_ID }));
          return;
        }
        // a .SC2Hotkeys upload is an SC2 source: select SC2 and make the upload active, so
        // the dropdown reflects what the board shows. Route to the B-side when a header B
        // upload or a drop-while-comparing targeted it, otherwise the A-side.
        setGameId("sc2");
        // route to the B-side only while actually comparing, so a stale "B" target can never
        // send a later import to the hidden B-side (the A board would silently not update)
        if (uploadTarget === "B" && comparing) {
          setUploadedB({ text, name });
          setSc2SourceBId(UPLOADED_ID);
        } else {
          setUploaded({ text, name });
          setSc2SourceId(UPLOADED_ID);
        }
        return;
      }
      setImported((prev) => ({ ...prev, [applied.result.keyboard.id]: applied.result }));
      setBoardId(applied.result.keyboard.id);
    },
    maxImportN,
  );

  // picking a Keyboard in the picker adopts it: it clears any share-link override and persists
  // the choice, so a shared board the viewer explicitly selects becomes their own
  const selectBoard = useCallback(
    (id: string) => {
      setLensOverride(null);
      setBoardId(id);
    },
    [setBoardId],
  );
  // picking a Keyboard Layout: while a share-link override is active, keep it ephemeral (update
  // the override, never storage) so the shared view stays a peek; otherwise persist per board
  const pickLayout = useCallback(
    (id: string) => {
      if (lensOverride) setLensOverride({ kbd: effBoardId, layout: id });
      else setLayoutSel((prev) => ({ ...prev, [boardId]: id }));
    },
    [lensOverride, effBoardId, boardId, setLayoutSel],
  );

  // rename targets the active board layer; ids are stable across renames, so the
  // selection map and the lensKey are untouched and the readout survives
  const activeIsBoardLayer = isBoardLayerMapping(keyboardLayout);
  const renameLayer = useCallback(
    (name: string) =>
      setImported((prev) => {
        const session = prev[effBoardId];
        return session
          ? { ...prev, [effBoardId]: renameBoardLayer(session, keyboardLayout.id, name) }
          : prev;
      }),
    [effBoardId, keyboardLayout.id, setImported],
  );

  // The Profile picker's options/value/handler, per side. SC2 lists its sources (TheCore right/left
  // plus an upload); a simple Game lists its Profiles plus an uploaded "my own" Profile, when the Game
  // has a parser (Destiny), and an optional "awaiting" entry. The control bar and the Compare A-side
  // share the A bundle (only the aria-label differs); the B-side is live for SC2 but inert ("Same as
  // A", no onChange) for a simple Game. One builder, so the two never drift.
  const setupPickerFor = (side: "A" | "B"): SetupPickerProps => {
    const isA = side === "A";
    // each side reads its OWN resolved setup; the two arms always agree in kind (setupB re-resolves
    // the same Game for SC2 and mirrors A's setup for a simple Game)
    const s = isA ? setup : setupB;
    if (s.kind === "sc2") {
      // split the flat sources out of the faceted list: an upload is "My setups", any other
      // non-faceted source (the board-bound Edennil) is "Personal". The B side drops pinned
      // sources (portableOnly), so its Personal family is simply empty.
      const uploads = s.sources
        .filter((src) => src.id === UPLOADED_ID)
        .map((src) => ({ id: src.id, name: src.name }));
      const personal = s.sources
        .filter((src) => !FACETED_SC2_IDS.has(src.id) && src.id !== UPLOADED_ID)
        .map((src) => ({ id: src.id, name: src.name }));
      return {
        ariaLabel: isA ? "Profile" : "Profile B",
        model: {
          kind: "faceted",
          activeId: s.source?.id ?? "",
          setups: FACETED_SC2_SETUPS,
          personal,
          uploads,
        },
        onChange: isA ? setSc2SourceId : setSc2SourceBId,
        onUpload: () => {
          setUploadTarget(side);
          pipeline.openModal("profile");
        },
        onNavigate,
      };
    }
    // a simple Game collapses to a bare menu: its Profiles (plus an uploaded "my own" one) and, when
    // the Game's file is parseable (Destiny), an upload target. B mirrors A's resolved Profile (inert).
    const model: SetupPickerProps["model"] = {
      kind: "simple",
      activeId: s.profile?.id ?? "",
      setups: [],
      personal: [],
      uploads: [],
      simpleLabel: `${game.name} profiles`,
      simpleProfiles: s.profiles.map((p) => ({ id: p.id, name: p.name })),
      uploadHint: game.uploadFormat ? `Upload your ${game.uploadFormat.pasteLabel}` : undefined,
    };
    return isA
      ? {
          ariaLabel: "Profile",
          model,
          onChange: (id) => setProfileSel((prev) => ({ ...prev, [gameId]: id })),
          onUpload: game.ingestUpload
            ? () => {
                setUploadTarget("A");
                pipeline.openModal("profile");
              }
            : undefined,
        }
      : { ariaLabel: "Profile B", model };
  };
  const pickerA = setupPickerFor("A");
  const pickerB = setupPickerFor("B");

  // size both surfaces to one shared key unit and reserve the mouse chip columns, behind one hook
  const { reserve, unit, wrapRef } = useSurfaceSizing({
    comparing,
    table,
    tableB,
    layers,
    layersB,
    board,
    boardB,
  });

  // the import modal owns the keyboard while open. The lens keys reset the readout / hover by
  // profile name, so re-uploading a same-named file keeps the selection (the setup is the same);
  // switching board, layout, or to a differently-named file resets it - but never a modifier press.
  // Build submenus split the two: the hover key resets on every context change (a drill
  // rebuilds the bindings list, so a still-hovered worker row must not strand its flashlight), but
  // the readout shares the WORKER's lens so drilling into Build Basic does not wipe the "Build
  // Basic" readout the drilling keystroke just set. A simple Game has no submenus, so they coincide.
  const lensBase = `${effBoardId}:${keyboardLayout.id}:${setup.nameKey}:${roster}`;
  // include the flat Context so the readout re-keys when a simple Game switches On Foot <-> Vehicle
  const lensKey = `${lensBase}:${sc2 ? context : flatContextId}`;
  const readoutLensKey = sc2 ? `${lensBase}:${parentContext(roster, context) ?? context}` : lensKey;
  const { readout, press, pressCombo } = useReadout({
    layers,
    mods,
    keycaps,
    marks,
    lensKey: readoutLensKey,
    enabled: !pipeline.modalOpen,
  });
  // physical mouse buttons (held) + wheel (momentary flash) over the render area depress their zone
  // and read out, the same broad coverage as the keyboard. `press` reads out; `pressFlash`
  // gives the wheel its 240ms pulse.
  const mouseHeld = useMouseButtons(press, pressFlash, !pipeline.modalOpen);
  // caps/zones to show depressed: a click's / wheel's momentary 240ms flash UNION every
  // held keyboard code UNION every held mouse-button zone - so a held input stays down until released.
  // A held key's e.code IS the active code the cap renders under, so no translation.
  const pressedCodes = useMemo(() => {
    const codes = [...(pressed?.codes ?? []), ...held, ...mouseHeld];
    return codes.length ? codes : null;
  }, [pressed, held, mouseHeld]);

  // a hover flashlight strands when the list rebuilds under a still-hovered row (the
  // mouse never left it): clear hoverKey on the same lens change, the in-render reset
  // useReadout uses - keyed on lensKey, not the keycaps/layers a modifier press churns
  const [prevHoverLens, setPrevHoverLens] = useState(lensKey);
  if (prevHoverLens !== lensKey) {
    setPrevHoverLens(lensKey);
    setHoverKey(null);
  }

  // SC2 build-menu drill-down: drillNav for the cap-click path plus the physical-keyboard
  // opener/back listener, behind one hook. A non-SC2 Game no-ops.
  const drillNav = useDrillNav({
    sc2,
    layers,
    activeModifierLayer,
    roster,
    context,
    modalOpen: pipeline.modalOpen,
    setContextSel,
  });

  // clicking a modifier cap latches its modifier layer (the touch path); every cap click reads out
  // and depresses the cap, the same feedback a list-row click gives. An opener cap also drills into
  // its submenu. The cap reports its active code, which the modifier-latch
  // check reads directly.
  const pressCap = useCallback(
    (code: string, legend: string) => {
      if (modifierFamily(code)) toggleLatch(code);
      press(code, legend);
      pressFlash([code]);
      drillNav(code);
    },
    [toggleLatch, press, pressFlash, drillNav],
  );

  const toggleIsolate = useCallback((id: string) => setDimId((d) => (d === id ? null : id)), []);
  const clearHover = useCallback(() => setHoverKey(null), []);

  // a readout chip click latches that layer's modifiers, reads out the same key on it,
  // and depresses it - the same press feedback a row or cap click gives. The
  // latch drives the board; a physically-held modifier still wins until it is released.
  const selectChip = useCallback(
    (combo: string) => {
      if (!readout) return;
      latchCombo(combo);
      pressCombo(readout.code, combo);
      pressFlash([readout.code]);
    },
    [readout, latchCombo, pressCombo, pressFlash],
  );

  // a row click reads out the row's own combo (via its first key) and depresses all
  // its keys, the single press-feedback system
  const selectRow = useCallback(
    (row: ListRow) => {
      const first = row.keys[0];
      if (!first) return;
      pressCombo(first.code, row.combo);
      pressFlash(row.keys.map((k) => k.code));
    },
    [pressCombo, pressFlash],
  );

  // the board flashlight A and B: the search/hover codes off-compare, the diff flashlight
  // on-compare (each board lights its own changed positions on the face it shows)
  const { litA, litB } = useCompareFlashlight({
    diff,
    hoverDiffId,
    mods,
    comparing,
    flashlightCodes,
  });

  // the modifier-layer strip spans both sides' families while comparing (union)
  const profileModsB = useMemo(() => profileModifiers(layersB), [layersB]);
  const stripFamilies = useMemo(() => {
    if (!comparing) return profileMods;
    const set = new Set<string>([...profileMods, ...profileModsB]);
    return (["ctrl", "alt", "shift"] as const).filter((f) => set.has(f));
  }, [comparing, profileMods, profileModsB]);

  // a Changes-row click flashes the row's keys on both sides and latches the modifier
  // layer the row lives on (rowLatchCombo skips the base-first position so a base+mod
  // Command jumps to its mod face, not base), the single press-feedback system
  const selectDiffRow = useCallback(
    (r: DiffRow) => {
      pressFlash([...r.a.map((p) => p.code), ...r.b.map((p) => p.code)]);
      const combo = rowLatchCombo(r);
      if (combo) latchCombo(combo);
      else clearLatched();
    },
    [pressFlash, latchCombo, clearLatched],
  );

  const toggleCompare = useCallback(() => {
    setComparing((c) => !c);
    setHoverDiffId(null);
    // a stale A-side hover (masked while comparing) must not re-light when compare is
    // toggled back off; the lens-change guard does not fire on a compare toggle
    setHoverKey(null);
    // a stale B upload target must not route a later (non-compare) import to the hidden
    // B-side; the drop effect and the per-side Upload buttons re-point it as needed
    setUploadTarget("A");
  }, [setComparing, setHoverDiffId]);

  // a fresh drop routes to the B-side while comparing, the A-side otherwise; a drag into an
  // already-open modal keeps whatever side its upload button set (so it is not overridden)
  useEffect(() => {
    if (pipeline.dragging && !pipeline.modalOpen) setUploadTarget(comparing ? "B" : "A");
  }, [pipeline.dragging, pipeline.modalOpen, comparing]);

  // the share link for the current selection; null unless a shareable Preset is selected
  const shareUrl = useShareLink({
    downloadEntry,
    comparing,
    sc2SourceBId,
    effBoardId,
    keyboardLayoutId: keyboardLayout.id,
    boardBId,
    layoutBId: layoutBValue,
  });

  if (!board) return null;

  // the keyboard plate overflows its deck by this much on each side (mirrors Keyboard.tsx).
  // In compare, the board row is padded left by it so the header card's left edge lines up
  // with the plate's left edge (the keyboard's background, not its first key) - point 3.
  const platePad = Math.max(8, unit * 0.3);

  // disabled + rename gates for the Keyboard / Layout pickers, shared by the control bar and CompareView
  const pinned = activePin != null;
  const canRename = activeIsBoardLayer && importedEntry != null;

  // the two compare sides, each a finished CompareSide (controls + render bundle). Built here so the
  // CompareView just renders the same column twice; B offers the "Same as A" sentinels and follows the
  // A lens by default. Cheap to build off-compare (pure lookups); only read in the compare branch.
  const sideA: CompareSide = {
    tag: "A",
    setup: pickerA,
    keyboard: { value: effBoardId, onChange: selectBoard, disabled: pinned },
    layout: {
      groups: layoutGroups(entry),
      value: keyboardLayout.id,
      onChange: pickLayout,
      onRename: canRename ? renameLayer : undefined,
      disabled: pinned,
    },
    board,
    keycaps,
    zones,
    lit: litA,
    comboHasExact,
    plusNote: plusUnavailable,
  };
  const sideB: CompareSide = {
    tag: "B",
    setup: pickerB,
    keyboard: {
      // a mirrored board-bound Profile pins B's board too: show (and lock to) "Same as A"
      value: boardBPinned ? "" : (boardBId ?? ""),
      onChange: (id) => pickBoardB(id || null),
      sameAs: { label: `Same as A: ${board.name}` },
      disabled: boardBPinned,
    },
    layout: {
      groups: layoutGroups(entryB),
      value: layoutBValue ?? "",
      onChange: (id) => pickLayoutB(id || null),
      sameAs: {
        label: sameBoardB
          ? `Same as A: ${keyboardLayout.name}`
          : `Default: ${activeLayout(entryB, undefined).name}`,
      },
      ariaLabel: "Layout B",
    },
    board: boardB ?? board,
    keycaps: keycapsB,
    zones: zonesB,
    lit: litB,
    comboHasExact: comboHasExactB,
    plusNote: plusUnavailableB,
  };

  return (
    <div className="app">
      <AppHeader onNavigate={onNavigate}>
        {/* the universal action bar: Compare, Download, Link - identical for every game. The SC2-only
            view toggles (rapid-fire, defaults) live in the View card below, not here. */}
        <button
          type="button"
          className={comparing ? "compare-toggle on" : "compare-toggle"}
          aria-pressed={comparing}
          title="Compare two profiles"
          onClick={toggleCompare}
        >
          Compare
        </button>
        {/* the top-bar download: SC2's active Preset rides the catalog (locale-converted) path;
            a simple Game's built-in Profile downloads its vendored source verbatim. Keyboard/Profile
            uploads live beside their pickers; an uploaded Profile carries no source, so it shows none.
            downloadLayout comes from useSide - the SAME layout the rendered board's served text was
            converted through, so the picture and the file cannot drift (a Plus source serializes
            through its underlying universal layout there, never the derived layer's charmap). */}
        {downloadEntry && downloadLayout ? (
          <PresetDownload entry={downloadEntry} layout={downloadLayout} />
        ) : null}
        {profileDownload ? <ProfileDownload download={profileDownload} /> : null}
        {/* copy a link to this Preset + lens; shown only for a shareable selection */}
        {shareUrl ? <ShareLinkButton url={shareUrl} /> : null}
      </AppHeader>
      <div className="body">
        <div className="main-col">
          <div className="controlbar">
            {/* the Software card: Game + Profile (Game stays visible while comparing) */}
            <div className="ctl-strip">
              <div className="ctl-item">
                <span className="ctl-lbl">Game</span>
                <span className="ctl-select">
                  <select
                    aria-label="Game"
                    value={gameId}
                    onChange={(e) => setGameId(e.target.value)}
                  >
                    {gameOrder.map((id) => (
                      <option key={id} value={id}>
                        {builtinGames[id]?.name}
                      </option>
                    ))}
                  </select>
                </span>
              </div>
              {/* while comparing, Profile + Layout move into the per-side header cards and
                  the Keyboard is fixed, so this card shows Game alone and the Hardware card
                  below is hidden */}
              {!comparing ? (
                <>
                  <span className="ctl-div" />
                  <div className="ctl-item">
                    <span className="ctl-lbl">Profile</span>
                    {/* the faceted Setup picker: SC2 facets its sources by family + edition + axes; a
                        simple Game collapses to a bare Profile list. The TheCore "Learn" referral now
                        lives in the picker's own footer (its old sibling slot here is retired). */}
                    <SetupPicker {...pickerA} />
                  </div>
                </>
              ) : null}
            </div>
            {/* the Hardware card (the lens): Keyboard + Layout. Hidden while comparing - each
                side carries its own board + layout in its header card instead. The Software /
                Hardware split mirrors the model: a setup is Software projected onto
                Hardware. Cards are unlabelled for now; the grouping carries the meaning */}
            {!comparing ? (
              <div className="ctl-strip">
                <div className="ctl-item">
                  <span className="ctl-lbl">Keyboard</span>
                  <span className="ctl-select">
                    {/* the upload button beside it takes a keyboard file (KLE / VIA / QMK) */}
                    <KeyboardSelect
                      ariaLabel="Keyboard"
                      value={effBoardId}
                      onChange={selectBoard}
                      imported={imported}
                      disabled={activePin != null}
                    />
                    <UploadButton
                      title="Upload a keyboard (KLE / VIA / QMK)"
                      onClick={() => {
                        setUploadTarget("A");
                        pipeline.openModal("keyboard");
                      }}
                    />
                  </span>
                </div>
                <span className="ctl-div" />
                <div className="ctl-item">
                  <span className="ctl-lbl">Layout</span>
                  <span className="ctl-select">
                    <LayoutPick
                      groups={layoutGroups(entry)}
                      value={keyboardLayout.id}
                      onChange={pickLayout}
                      onRename={activeIsBoardLayer && importedEntry ? renameLayer : undefined}
                      disabled={activePin != null}
                    />
                  </span>
                </div>
              </div>
            ) : null}
            {/* strip 2: the SC2 command card, gated on the active source carrying
                command bindings - a [Hotkeys]-only upload has none, so no card */}
            {sc2 && table && Object.keys(table.commands).length > 0 ? (
              <div className="ctl-strip">
                <CommandCard
                  mode={mode}
                  roster={roster}
                  context={context}
                  contexts={contexts}
                  onMode={setMode}
                  onRoster={setRace}
                  onContext={setContextSel}
                />
              </div>
            ) : null}
            {/* strip 2 (simple Games): the flat-Context selector, shown when the active Profile has
                more than one Context - Destiny's On Foot / Vehicle */}
            {flatContexts ? (
              <div className="ctl-strip">
                <ContextSelect
                  contexts={flatContexts}
                  value={flatContextId}
                  onChange={(id) => setFlatContextSel((prev) => ({ ...prev, [gameId]: id }))}
                />
              </div>
            ) : null}
            {/* the View card: the board view-state controls - Layer (the modifier-layer latches;
                while comparing the families span both sides) and the SC2-only options (rapid-fire +
                the relabelled defaults), split by the house divider. It changes HOW the board renders,
                not what is loaded, so it is a sibling card to the source strips - it flows after them
                and wraps LEFT like any other strip (never margin-left:auto, which stranded the old
                floating group). Rendered only when a group has content; the divider only when both do.
                Rapid-fire and Defaults gate together (table != null is equivalent to sc2 &&
                activeText != null for the live A side), so one gate carries the whole SC2 group. */}
            {stripFamilies.length > 0 || (sc2 && activeText != null) ? (
              <div className="ctl-strip view">
                {stripFamilies.length > 0 ? (
                  <ModifierStrip
                    families={stripFamilies}
                    mods={mods}
                    onToggle={toggleModifier}
                    onBase={clearLatched}
                  />
                ) : null}
                {stripFamilies.length > 0 && sc2 && activeText != null ? (
                  <span className="ctl-div" />
                ) : null}
                {sc2 && activeText != null ? (
                  <div className="ctl-item">
                    <span className="ctl-lbl">SC2 Options</span>
                    <RapidFireToggle on={rapidFireOn} onToggle={() => setRapidFireOn((o) => !o)} />
                    <DefaultsToggle on={hideDefaults} onToggle={() => setHideDefaults((o) => !o)} />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          <main
            className="canvas"
            style={comparing ? ({ "--kb-bed": "transparent" } as CSSProperties) : undefined}
          >
            {comparing ? (
              <CompareView
                wrapRef={wrapRef}
                unit={unit}
                platePad={platePad}
                mods={mods}
                reserve={reserve}
                pressedCodes={pressedCodes}
                onPress={pressCap}
                imported={imported}
                iconFor={sc2 ? iconGlyph : undefined}
                a={sideA}
                b={sideB}
              />
            ) : (
              <>
                {plusUnavailable ? <PlusNote reason={plusUnavailable} /> : null}
                <div ref={wrapRef} className="kb-wrap" style={{ gap: 0.5 * unit }}>
                  <Surfaces
                    board={board}
                    keycaps={keycaps}
                    zones={zones}
                    litCodes={litA}
                    dimCategories={dimMembers}
                    dimFallback={comboHasExact}
                    unit={unit}
                    mods={mods}
                    reserve={reserve}
                    pressedCodes={pressedCodes}
                    onPress={pressCap}
                    iconFor={sc2 ? iconGlyph : undefined}
                  />
                </div>
                <Readout readout={readout} categories={categories} onChip={selectChip} />
              </>
            )}
          </main>
        </div>
        {comparing ? (
          <ChangesList
            diff={diff ?? { rows: [], same: 0 }}
            categories={categories}
            keycapsA={keycaps}
            keycapsB={keycapsB}
            hoverId={hoverDiffId}
            onHoverRow={(r) => setHoverDiffId(r.id)}
            onLeaveRow={() => setHoverDiffId(null)}
            onSelectRow={selectDiffRow}
          />
        ) : listOpen ? (
          <BindingsList
            layers={layers}
            keycaps={keycaps}
            categories={categories}
            groups={groups}
            dimId={activeDimId}
            hoverKey={hoverKey}
            marks={marks}
            onHover={setHoverKey}
            onLeave={clearHover}
            onSelect={selectRow}
            onIsolate={toggleIsolate}
            onCollapse={() => {
              setHoverKey(null);
              setListOpen(false);
            }}
            onSearch={setSearchCodes}
          />
        ) : (
          <button
            type="button"
            className="kl-list-reopen"
            title="Show bindings"
            aria-label="Show bindings"
            onClick={() => setListOpen(true)}
          >
            <svg
              aria-hidden="true"
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 6l-6 6 6 6M18 6l-6 6 6 6" />
            </svg>
            <span className="kl-list-reopen-label">Bindings</span>
          </button>
        )}
      </div>
      {pipeline.dragging ? <DropOverlay /> : null}
      <ImportModal
        open={pipeline.modalOpen}
        target={pipeline.target}
        profileFormat={game.uploadFormat ?? null}
        preview={pipeline.pending?.preview ?? null}
        error={pipeline.error}
        awaiting={pipeline.awaiting}
        onText={pipeline.handleText}
        onFiles={pipeline.handleFiles}
        onApply={pipeline.apply}
        onClose={pipeline.closeModal}
      />
      <Toasts toasts={pipeline.toasts} onDismiss={pipeline.dismissToast} />
    </div>
  );
}
